import { Navigate } from '@starsoft/common/components';
import {
  CreateDepositRequestDto,
  CreateKycRequestDto,
} from '@starsoft/common/dto';
import {
  CommonModalsKey,
  DepositStep,
  KycCheckpointId,
  KYCStatus,
} from '@starsoft/common/enums';
import {
  useKycFormSubmission,
  useKycNavigation,
  useModal,
  useModalNavigation,
  useSearchParams,
} from '@starsoft/common/hooks';
import { CoinType, SettingLayoutComponentId } from '@starsoft/common/models';
import { AnimatePresence, motion } from 'framer-motion';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useDeposit from '../../../../../api/deposit/mutations/useDeposit';
import {
  useDepositForm,
  useDepositFormState,
} from '../../../../../hooks/deposit';
import useDepositSearchParams from '../../../../../hooks/deposit/useDepositSearchParams';
import useTransactionsTabNavigation from '../../../../../hooks/useTransactionsTabNavigation';
import TransactionModalsLayoutFooter from '../../../Common/Layout/Footer';
import TransactionsModalsLayoutHeader from '../../../Common/Layout/Header';
import TransactionModalsContentLayoutTabs from '../../../Common/Layout/Tabs';
import { fadeInRight } from '../../../Common/variants';
import { TransactionModalsDepositContentLayoutProps } from './props';
import styles from './styles.module.scss';

/**
 * TransactionModalsDepositContentLayout Component
 * This component is responsible for rendering the layout of the deposit modal content.
 * It manages the form state, handles KYC submission, and manages navigation between steps.
 *
 * @param {PropsWithChildren} props - The properties for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the layout.
 *
 * @returns {JSX.Element} The rendered layout component for the deposit modal content.
 *
 * @example
 * // Usage example:
 * <TransactionModalsDepositContentLayout>
 *   <div>Deposit Content</div>
 * </TransactionModalsDepositContentLayout>
 */
export default function TransactionModalsDepositContentLayout({
  children,
  originId,
  version,
}: PropsWithChildren<TransactionModalsDepositContentLayoutProps>) {
  // Retrieve modal close function for the 'Deposit' modal
  const { onClose } = useModal(CommonModalsKey.Deposit);
  // Retrieve deposit search parameters and methods for setting them
  const { transactionStep, coinType, handleSetDepositSearchParams, coinId } =
    useDepositSearchParams();
  // Retrieve modal navigation link generator
  const { getModalLink } = useModalNavigation();
  // Retrieve search parameters from the URL
  const search: URLSearchParams = useSearchParams();
  // Determine if styles should be disabled based on the current transaction step
  const disableStyles: boolean =
    transactionStep == DepositStep.Form ||
    transactionStep === DepositStep.Response;

  // Initialize form methods with default values
  const methods = useForm<CreateDepositRequestDto>({
    defaultValues: {
      amount: 20,
      coinId,
    },
  });

  // Destructure form methods
  const { handleSubmit, watch, setValue } = methods;

  // Retrieve deposit form state and methods
  const {
    isFormLoading,
    formError,
    useKycMeByOriginIdQuery: { kyc },
    coinMapping,
    showExternalLink,
    accountByCoinQuery: { currentAccount },
    coinOptions,
    isValidCoinId,
  } = useDepositForm(watch, setValue);

  // Determine if the submit button should be disabled based on the form state
  const isSubmitButtonDisabled: boolean = useDepositFormState({
    watch,
    setValue,
  });

  // Retrieve deposit mutation and loading state
  const { isLoading: isLoadigCreateDeposit, deposit } = useDeposit();

  // Retrieve KYC form submission handler and loading state
  const { handleKycSubmission, isLoadingKycSubmission } = useKycFormSubmission({
    coinId,
    kyc,
    methods,
    enabled: transactionStep === DepositStep.CreatePersonalIdentity,
  });

  // Retrieve KYC navigation handlers
  const { handleSetKycStep, handleSetKycAndDepositStep } = useKycNavigation({
    coinId,
  });

  // Retrieve tab navigation handler
  const { setTab } = useTransactionsTabNavigation({ setValue });
  // Watch the 'amount' field in the form
  const amount = watch('amount');

  /**
   * handleBackButton Function
   * Handles the back button click event to navigate to the previous step or close the modal.
   *
   * @returns {void}
   *
   * @example
   * handleBackButton();
   */
  const handleBackButton = useCallback(() => {
    const paths = Object.values(DepositStep);
    const currentIndex = paths.findIndex(path => path == transactionStep);

    if (currentIndex == 0) {
      onClose();
    }

    let targetPath = paths[currentIndex - 1];

    handleSetDepositSearchParams('step', targetPath);
  }, [transactionStep, handleSetDepositSearchParams, onClose]);

  /**
   * handleFiatDeposit Function
   * Handles the deposit process for fiat currency.
   *
   * @param {CreateDepositRequestDto} data - The data for creating the deposit request.
   * @returns {void}
   *
   * @example
   * handleFiatDeposit({ amount: 100, coinId: '1' });
   */
  function handleFiatDeposit(data: CreateDepositRequestDto) {
    if (!coinMapping?.origin) {
      deposit(data);
      return;
    }

    if (!kyc) {
      handleSetKycAndDepositStep(
        KycCheckpointId.Identity,
        DepositStep.CreatePersonalIdentity,
      );
      return;
    }

    if (kyc?.status !== KYCStatus.Verified) {
      handleSetKycAndDepositStep(
        kyc?.stage?.checkpoint?.id,
        DepositStep.CreatePersonalIdentity,
      );
      return;
    }

    deposit(data);
  }

  /**
   * handleDepositFormStep Function
   * Handles the deposit form submission based on the coin type.
   *
   * @param {CreateDepositRequestDto} data - The data for creating the deposit request.
   * @returns {void}
   *
   * @example
   * handleDepositFormStep({ amount: 100, coinId: '1' });
   */
  function handleDepositFormStep(data: CreateDepositRequestDto) {
    switch (coinType) {
      case CoinType.Fiat:
        handleFiatDeposit(data);
        break;
      case CoinType.Crypto:
        break;
    }
  }

  /**
   * handleCreateKycAndDeposit Function
   * Handles the creation of KYC and deposit process.
   *
   * @param {CreateDepositRequestDto} data - The data for creating the deposit request.
   * @returns {Promise<void>}
   *
   * @example
   * await handleCreateKycAndDeposit({ amount: 100, coinId: '1' });
   */
  async function handleCreateKycAndDeposit(
    data: CreateDepositRequestDto & CreateKycRequestDto,
  ) {
    try {
      if (!kyc || kyc?.status != KYCStatus.Verified) {
        const createdkyc = await handleKycSubmission(
          data as CreateKycRequestDto,
        );

        if (createdkyc?.status == KYCStatus.Verified) {
          deposit(data);
          return;
        }

        if (createdkyc) {
          handleSetKycStep(createdkyc?.stage?.checkpoint?.id);
          return;
        }
      }

      if (kyc && kyc?.status != KYCStatus.Verified) {
        handleSetKycAndDepositStep(
          kyc?.stage?.checkpoint?.id,
          DepositStep.CreatePersonalIdentity,
        );
        return;
      }

      if (kyc?.status == KYCStatus.Verified) {
        deposit(data);
      }
      // eslint-disable-next-line no-empty
    } catch (err: unknown) {}
  }

  /**
   * onSubmit Function
   * Handles the form submission based on the current transaction step.
   *
   * @param {CreateDepositRequestDto} data - The data for creating the deposit request.
   * @returns {Promise<void>}
   *
   * @example
   * await onSubmit({ amount: 100, coinId: '1' });
   */
  async function onSubmit(data: CreateDepositRequestDto) {
    try {
      switch (transactionStep) {
        case DepositStep.Form:
          handleDepositFormStep(data);
          break;
        case DepositStep.CreatePersonalIdentity:
          await handleCreateKycAndDeposit(data);
          break;
        case DepositStep.Response:
          break;
      }
      //eslint-disable-next-line
    } catch (err: unknown) {}
  }

  /**
   * handleInvalidCoinId Function
   * Handles the case when the coin ID is invalid by setting a valid coin ID.
   *
   * @returns {void}
   *
   * @example
   * handleInvalidCoinId();
   */
  function handleInvalidCoinId(): void {
    if (isValidCoinId || !coinOptions || coinOptions?.length === 0) {
      return;
    }

    handleSetDepositSearchParams('coin-id', coinOptions[0].id);
  }

  // Effect to handle invalid coin ID
  useEffect(handleInvalidCoinId, [
    coinOptions,
    isValidCoinId,
    handleSetDepositSearchParams,
  ]);

  // Redirect to the form step if the amount is not set or is zero
  if (
    ![DepositStep.Form, DepositStep.Response].includes(transactionStep) &&
    (!amount || amount == 0)
  ) {
    search.set('step', DepositStep.Form);
    search.delete('method-id');
    return (
      <Navigate
        to={`${getModalLink(CommonModalsKey.Deposit, `?${search.toString()}`)}`}
        replace
      />
    );
  }

  // Render the layout with header, tabs, form, and footer
  return (
    <div
      className={`${styles.container} ${version === SettingLayoutComponentId.ModalV2?.toString() ? styles['container--v2'] : ''}`}
    >
      <TransactionsModalsLayoutHeader
        handleBackButton={handleBackButton}
        onClose={onClose}
        version={version}
        transactionStep={transactionStep}
      />

      <TransactionModalsContentLayoutTabs
        currentTab={coinType}
        onTabChange={setTab}
        version={version}
        originId={originId}
        hidden={![DepositStep.Form].includes(transactionStep)}
      />
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id="deposit"
          className={styles.container__content}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={transactionStep}
              variants={fadeInRight}
              initial="hidden"
              animate="show"
              className={`${styles.container__content__wrapper}  ${disableStyles ? styles['container__content__wrapper--disable-styles'] : ''}`}
            >
              {children}
            </motion.div>
          </AnimatePresence>
          <TransactionModalsLayoutFooter
            coin={currentAccount?.coin}
            amount={amount}
            coinType={coinType}
            i18nNamespace="deposit"
            transactionStep={transactionStep}
            isLoadingSubmit={isLoadigCreateDeposit || isLoadingKycSubmission}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            isLoading={isFormLoading}
            error={formError}
            showExternalLink={showExternalLink}
          />
        </form>
      </FormProvider>
    </div>
  );
}
